import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";
import { api, STATUS_LABELS, STATUS_ORDER, fmtUSD } from "../lib/api";

export default function AdminPage() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [apps, setApps] = useState([]);
  useSeo({ title: "Admin — Brand and Behavior", noindex: true });

  const refresh = async () => {
    if (!user || user.role !== "admin") return;
    const [o, r, a] = await Promise.all([
      api.get("/admin/orders"),
      api.get("/admin/requests"),
      api.get("/admin/applications"),
    ]);
    setOrders(o.data); setRequests(r.data); setApps(a.data);
  };

  useEffect(() => { refresh(); }, [user, refresh]);

  if (loading) return <main className="app-main"><section className="page-hero"><h1>{t("Loading…", "Cargando…")}</h1></section></main>;
  if (!user) return <main className="app-main"><section className="page-hero"><h1>{t("Sign in required", "Iniciar sesión requerido")}</h1></section></main>;
  if (user.role !== "admin") return <main className="app-main"><section className="page-hero"><h1>{t("Admin only", "Solo admin")}</h1></section></main>;

  return (
    <main className="app-main" data-testid="admin-page">
      <section className="dash-header">
        <div className="h-greet">Admin</div>
        <div className="h-email">{user.email}</div>
      </section>
      <div className="dash-tabs">
        <a className={tab === "orders" ? "active" : ""} onClick={() => setTab("orders")} data-testid="admin-tab-orders">Orders ({orders.length})</a>
        <a className={tab === "requests" ? "active" : ""} onClick={() => setTab("requests")} data-testid="admin-tab-requests">Requests ({requests.length})</a>
        <a className={tab === "applications" ? "active" : ""} onClick={() => setTab("applications")} data-testid="admin-tab-applications">Applications ({apps.length})</a>
      </div>

      {tab === "orders" && (
        <section>
          {orders.length === 0 ? <div className="empty">No orders.</div> : orders.map((o) => (
            <AdminOrderRow key={o.order_id} order={o} onChange={refresh} />
          ))}
        </section>
      )}
      {tab === "requests" && (
        <section>
          {requests.length === 0 ? <div className="empty">No requests.</div> : requests.map((r) => (
            <AdminRequestRow key={r.request_id} req={r} onChange={refresh} />
          ))}
        </section>
      )}
      {tab === "applications" && (
        <section>
          {apps.length === 0 ? <div className="empty">No applications.</div> : apps.map((a) => (
            <div className="order-row" key={a.application_id}>
              <div>
                <div className="meta">{a.company}</div>
                <div className="title">{a.name}</div>
                <div className="meta" style={{ marginTop: 6 }}>{a.email}</div>
              </div>
              <div style={{ fontSize: 14, color: "var(--muted)", maxWidth: 480, lineHeight: 1.45 }}>{a.why_brand_office}</div>
              <div className="meta">{new Date(a.created_at).toLocaleDateString()}</div>
              <div>{a.budget_confirmed ? <span className="status-pill paid">€</span> : <span className="status-pill">€?</span>}</div>
            </div>
          ))}
        </section>
      )}
    </main>
  );
}

function AdminOrderRow({ order, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ status: order.status || "", deliverable_url: order.deliverable_url || "", deliverable_notes: order.deliverable_notes || "", next_step_recommendation: order.next_step_recommendation || "", needs_clarification: order.needs_clarification || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.order_id}`, form);
      await onChange(); setOpen(false);
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ borderBottom: "1px solid var(--line)" }} data-testid={`admin-order-${order.order_id}`}>
      <div className="order-row" style={{ borderBottom: 0, cursor: "pointer" }} onClick={() => setOpen((o) => !o)}>
        <div>
          <div className="meta">{order.service_slug} · {order.order_id}</div>
          <div className="title">{order.service_name}</div>
          <div className="meta" style={{ marginTop: 6 }}>{order.user_email}</div>
        </div>
        <div className="meta">{STATUS_LABELS[order.status]?.en || order.status}</div>
        <div className="meta">{fmtUSD(order.amount)}</div>
        <div><span className={`status-pill ${order.payment_status === "paid" ? "paid" : ""}`}>{order.payment_status}</span></div>
      </div>
      {open && (
        <div style={{ padding: "0 22px 22px", display: "grid", gap: 12 }}>
          <div className="form-row"><label>Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid={`admin-status-${order.order_id}`}>
              {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s].en}</option>)}
            </select>
          </div>
          <div className="form-row"><label>Deliverable URL</label><input value={form.deliverable_url} onChange={(e) => setForm({ ...form, deliverable_url: e.target.value })} /></div>
          <div className="form-row"><label>Deliverable notes</label><textarea value={form.deliverable_notes} onChange={(e) => setForm({ ...form, deliverable_notes: e.target.value })} /></div>
          <div className="form-row"><label>Next step recommendation</label><textarea value={form.next_step_recommendation} onChange={(e) => setForm({ ...form, next_step_recommendation: e.target.value })} /></div>
          <div className="form-row"><label>Needs clarification (client-visible)</label><textarea value={form.needs_clarification} onChange={(e) => setForm({ ...form, needs_clarification: e.target.value })} /></div>
          <div>
            <button className="button" disabled={saving} onClick={save} data-testid={`admin-save-${order.order_id}`}>{saving ? "Saving…" : "Save"}</button>
          </div>
          {order.intake_universal && (
            <details style={{ fontSize: 13, color: "var(--muted)" }}>
              <summary>Intake (universal)</summary>
              <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(order.intake_universal, null, 2)}</pre>
            </details>
          )}
          {order.intake_specific && (
            <details style={{ fontSize: 13, color: "var(--muted)" }}>
              <summary>Intake (specific)</summary>
              <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(order.intake_specific, null, 2)}</pre>
            </details>
          )}
          <AdminOrderFiles orderId={order.order_id} />
          <AdminUploadDeliverable orderId={order.order_id} />
          <AdminMessages orderId={order.order_id} adminEmail={order.user_email} />
        </div>
      )}
    </div>
  );
}

function AdminRequestRow({ req, onChange }) {
  const [form, setForm] = useState({ status: req.status, admin_response: req.admin_response || "" });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/requests/${req.request_id}`, form); await onChange(); setOpen(false); }
    catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ borderBottom: "1px solid var(--line)" }} data-testid={`admin-req-${req.request_id}`}>
      <div className="order-row" style={{ borderBottom: 0, cursor: "pointer" }} onClick={() => setOpen((o) => !o)}>
        <div>
          <div className="meta">{req.request_type} · {req.priority}</div>
          <div className="title">{req.title}</div>
        </div>
        <div className="meta">{req.status}</div>
        <div className="meta">{new Date(req.created_at).toLocaleDateString()}</div>
        <div><span className="status-pill">{req.admin_response ? "✓" : "…"}</span></div>
      </div>
      {open && (
        <div style={{ padding: "0 22px 22px", display: "grid", gap: 12 }}>
          <p style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}><b>Context:</b> {req.context}</p>
          <p style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}><b>Assets:</b> {req.assets}</p>
          <p style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}><b>Decision needed:</b> {req.decision_needed}</p>
          <div className="form-row"><label>Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {["submitted","needs_clarification","active","client_reply","closed"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-row"><label>Response</label><textarea value={form.admin_response} onChange={(e) => setForm({ ...form, admin_response: e.target.value })} /></div>
          <div><button className="button" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button></div>
        </div>
      )}
    </div>
  );
}


function AdminOrderFiles({ orderId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${orderId}/files`).then((r) => { setFiles(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [orderId]);

  if (loading) return null;
  if (!files.length) return <details style={{ fontSize: 13, color: "var(--muted)" }}><summary>Files (0)</summary><div style={{ padding: 8 }}>No files uploaded.</div></details>;

  const download = async (file) => {
    try {
      const resp = await api.get(`/orders/${orderId}/files/${file.file_id}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: file.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Download failed");
    }
  };

  return (
    <details open style={{ fontSize: 13, color: "var(--ink)" }} data-testid={`admin-files-${orderId}`}>
      <summary>Client files ({files.length})</summary>
      <ul className="file-list" style={{ marginTop: 8 }}>
        {files.map((f) => (
          <li key={f.file_id} className="file-row">
            <span className="file-name">{f.original_filename}</span>
            <span className="file-size">{(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString()}</span>
            <button type="button" className="file-remove" onClick={() => download(f)} data-testid={`admin-download-${f.file_id}`}>Download</button>
          </li>
        ))}
      </ul>
    </details>
  );
}

function AdminUploadDeliverable({ orderId }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(0);

  const onPick = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setErr(""); setUploading(true);
    let successCount = 0;
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/orders/${orderId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        successCount += 1;
      } catch (e2) {
        setErr(`${f.name}: ${e2?.response?.data?.detail || e2.message}`);
      }
    }
    setUploading(false);
    setOk((n) => n + successCount);
    e.target.value = "";
  };

  return (
    <div className="admin-panel" data-testid={`admin-upload-${orderId}`}>
      <h4>Deliver work to client</h4>
      <div className="admin-upload">
        <label className="upload-btn" data-testid={`admin-upload-btn-${orderId}`}>
          <input type="file" multiple onChange={onPick} disabled={uploading} style={{ display: "none" }}
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.ai,.psd,.fig,.sketch,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.zip" />
          {uploading ? "Uploading…" : "Upload deliverable(s)"}
        </label>
        <span className="upload-hint">Files appear immediately in the client&apos;s dashboard.</span>
        {ok > 0 && <span style={{ fontSize: 12, color: "var(--accent-ink)" }}>✓ {ok} uploaded</span>}
      </div>
      {err && <div className="banner-warning" style={{ marginTop: 12 }}>{err}</div>}
    </div>
  );
}

function AdminMessages({ orderId, adminEmail }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [meEmail, setMeEmail] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/messages`);
      setMsgs(data);
    } catch (e) { void e; }
  }, [orderId]);

  useEffect(() => {
    refresh();
    api.get("/auth/me").then((r) => setMeEmail(r.data?.email || "")).catch(() => {});
  }, [refresh]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/orders/${orderId}/messages`, { body: body.trim() });
      setBody("");
      await refresh();
    } catch (e2) { void e2; }
    finally { setSending(false); }
  };

  return (
    <div className="admin-panel admin-messages" data-testid={`admin-messages-${orderId}`}>
      <h4>Messages with {adminEmail}</h4>
      <div className="messages-list">
        {msgs.length === 0 && <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>No messages yet.</p>}
        {msgs.map((m) => (
          <div key={m.message_id} className={`msg-row ${m.author_email === meEmail ? "msg-mine" : ""}`}>
            <div className="msg-meta">
              <span>{m.author_role === "admin" ? "You (admin)" : m.author_email}</span>
              <span>{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <div className="msg-body">{m.body}</div>
          </div>
        ))}
      </div>
      <form className="messages-input" onSubmit={send}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a reply to the client…"
          data-testid={`admin-msg-input-${orderId}`}
          disabled={sending}
        />
        <button type="submit" className="button" disabled={sending || !body.trim()} data-testid={`admin-msg-send-${orderId}`}>
          {sending ? "Sending…" : "Send"}
        </button>
      </form>
    </div>
  );
}
